import { useRouter } from "next/router";
import React, { useCallback, useContext, useMemo, useState } from "react";
import styled, { css } from "styled-components";

import * as T from "@/components/Typography";
import * as UserContext from "@/components/UserContext";
import * as U from "@/components/Utils";
import LoginForm from "@/forms/LoginForm";
import RegisterForm from "@/forms/RegisterForm";
import { colours, stringifyQueryParams } from "@/utils";
import FloatingTextBox from "@/components/marketing/FloatingTextBox";

type StyleProps = {
  borderLeft?: number;
  borderRight?: number;
};

const Signup: React.FC = () => {
  const { isLoggedIn, stringifiedParams } = useMembershipSignup();

  return (
    <S.Container>
      <S.Column borderRight={1}>
        <TT.CS>Join AllBright today!</TT.CS>
        {isLoggedIn === false && (
          <RegisterForm
            onSuccessUrl={`/memberships/pay${stringifiedParams}`}
            email={""}
            embeddedPage
          />
        )}
        <S.ResponsiveDivider />
      </S.Column>
      <S.Column borderLeft={1}>
        <S.LoginContainer>
          <TT.CS>Already a member?</TT.CS>
          <U.Space height={40} />
          {isLoggedIn === false && (
            <LoginForm
              onSuccessUrl={`/memberships/pay${stringifiedParams}`}
              email={""}
            />
          )}
          <S.Space />
          <U.Divider />
          <S.Space />
          <TT.CS paddingTop={10}>Not ready to sign up yet?</TT.CS>
          <S.ButtonContainer>
            <FloatingTextBox
              colour={colours.marketingOrange}
              backgroundColour={colours.white}
              copy=""
              linkText="Continue"
              linkUrl={`/memberships/pay${stringifiedParams}`}
              title="Checkout as guest"
            />
          </S.ButtonContainer>
        </S.LoginContainer>
      </S.Column>
    </S.Container>
  );
};

const useMembershipSignup = () => {
  const router = useRouter();
  const userInfo = useContext(UserContext.Context);
  const isLoggedIn = useMemo(() => Boolean(userInfo?.id), [userInfo]);
  const [stringifiedParams, setStringifiedParams] = useState("");

  const navigateToMembershipsPay = useCallback(() => {
    if (isLoggedIn) {
      router.push({
        pathname: "/memberships/pay",
        query: router.query,
      });
    }
  }, [isLoggedIn, router.query]);

  React.useEffect(() => {
    navigateToMembershipsPay();
  }, [navigateToMembershipsPay]);

  React.useEffect(() => {
    if (router.query) {
      setStringifiedParams(stringifyQueryParams(router.query));
    }
  }, [router.query]);

  return { isLoggedIn, stringifiedParams };
};

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;

    @media (min-width: 767px) {
      flex-direction: row;
    }
  `,

  Column: styled.div<StyleProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 20px 0;
    background-color: #f9f4f0;
    overflow-x: hidden;
    border-color: #aaa;
    border-width: 0;
    ${(props) =>
      props.borderLeft
        ? css`
            border-width: ${props.borderLeft}px;
            border-left-style: solid;
          `
        : css`
            border-left-style: none;
          `};

    ${(props) =>
      props.borderRight
        ? css`
            border-width: ${props.borderRight}px;
            border-right-style: solid;
          `
        : css`
            border-right-style: none;
          `};

    @media (min-width: 767px) {
      width: 50%;
    }
  `,

  LoginContainer: styled.div`
    flex: 1;
    width: 100%;
    height: 100%;
    padding: 20px;

    @media (min-width: 767px) {
      padding: 0 20%;
    }
  `,

  ButtonContainer: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
    padding: 0 50px;
  `,

  Space: styled(U.Space)`
    height: 20px;

    @media (min-width: 767px) {
      height: 40px;
    }
  `,

  ResponsiveDivider: styled(U.Divider)`
    width: 90%;

    @media (min-width: 767px) {
      border-size: 0;
      border-color: transparent;
    }
  `,
};

const TT = {
  CS: styled(T.CS)`
    font-weight: 700;
    opacity: 1;
    letter-spacing: 1px;
    margin-top: 10px;
    margin-bottom: 20px;
    text-align: center;
    text-transform: uppercase;
  `,
};

export default Signup;
